interface ReportDraftEntry {
  specimenId: string;
}

interface ReportDraftSpecimen {
  id: string;
  isSignedOff?: boolean;
}

interface ReportDraftPayloadOptions<
  Diagnosis extends ReportDraftEntry,
  Grossing extends ReportDraftEntry
> {
  diagnoses: Diagnosis[];
  grossing: Grossing[];
  specimens: ReportDraftSpecimen[];
  retainedSpecimenIds?: readonly string[];
  isPartiallyFinalized: boolean;
  frozenInterpretation: string;
}

interface ReportDraftPayload<
  Diagnosis extends ReportDraftEntry,
  Grossing extends ReportDraftEntry
> {
  diagnoses: Diagnosis[];
  grossing: Grossing[];
  caseLevelData?: { frozenInterpretation: string };
}

function getMutableSpecimenIds(
  specimens: ReportDraftSpecimen[],
  retainedSpecimenIds?: readonly string[]
): Set<string> {
  const unsignedSpecimenIds = new Set(
    specimens
      .filter((specimen) => !specimen.isSignedOff)
      .map((specimen) => specimen.id)
  );
  if (!retainedSpecimenIds) return unsignedSpecimenIds;
  return new Set(
    retainedSpecimenIds.filter((specimenId) =>
      unsignedSpecimenIds.has(specimenId)
    )
  );
}

function filterMutableEntries<Entry extends ReportDraftEntry>(
  entries: Entry[],
  mutableSpecimenIds: Set<string>
): Entry[] {
  return entries.filter((entry) => mutableSpecimenIds.has(entry.specimenId));
}

export function buildPathologistReviewDraftPayload<
  Diagnosis extends ReportDraftEntry,
  Grossing extends ReportDraftEntry
>(
  options: ReportDraftPayloadOptions<Diagnosis, Grossing>
): ReportDraftPayload<Diagnosis, Grossing> {
  const mutableSpecimenIds = getMutableSpecimenIds(
    options.specimens,
    options.retainedSpecimenIds
  );
  const payload: ReportDraftPayload<Diagnosis, Grossing> = {
    diagnoses: filterMutableEntries(options.diagnoses, mutableSpecimenIds),
    grossing: filterMutableEntries(options.grossing, mutableSpecimenIds),
  };
  if (!options.isPartiallyFinalized) {
    payload.caseLevelData = {
      frozenInterpretation: options.frozenInterpretation,
    };
  }
  return payload;
}
